/**
 * Everything this app asks the API about submissions and handovers.
 *
 * The only caller of `client` for this slice — routes and components go
 * through `queries.ts`, never straight to `fetch`.
 */

import { client } from "@/lib/api/client";
import type { components } from "@/lib/api/schema";

export type Submission = components["schemas"]["SubmissionOut"];
/** A queue row: a submission plus `subject` and `item_count`. */
export type SubmissionListItem = components["schemas"]["SubmissionListItem"];
export type SubmissionPage = components["schemas"]["SubmissionPage"];
export type SubmissionReview = components["schemas"]["SubmissionReviewOut"];
export type ReviewItem = components["schemas"]["ReviewItemOut"];
export type ValueChange = components["schemas"]["ValueChangeOut"];
export type Handover = components["schemas"]["HandoverOut"];
export type HandoverPage = components["schemas"]["HandoverPage"];

/** Which queue. `undefined` lets the server pick from the caller's
 *  grants and report back which one it chose. */
export type SubmissionScope = "mine" | "submitted" | "all";

export interface ListSubmissionsParams {
  scope?: SubmissionScope | undefined;
  status?: string | undefined;
  kind?: string | undefined;
  masterTypeId?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

export function listSubmissions(params: ListSubmissionsParams = {}): Promise<SubmissionPage> {
  return client.request<SubmissionPage>("/api/v1/submissions", {
    query: {
      scope: params.scope,
      status: params.status,
      kind: params.kind,
      master_type_id: params.masterTypeId,
      cursor: params.cursor,
      limit: params.limit,
    },
  });
}

export type SubmissionCount = components["schemas"]["SubmissionCountOut"];

/** How many submissions `listSubmissions` would list with these same
 *  filters — the dashboard's approvals-summary tile. */
export function countSubmissions(
  params: {
    scope?: SubmissionScope | undefined;
    status?: string | undefined;
    kind?: string | undefined;
  } = {},
): Promise<SubmissionCount> {
  return client.request<SubmissionCount>("/api/v1/submissions/count", {
    query: { scope: params.scope, status: params.status, kind: params.kind },
  });
}

/**
 * One submission, everything it would change, and whether this caller
 * may approve it.
 *
 * `can_decide` and `refusal` come from the server rather than being
 * re-derived from `can()`. The client's permission table can tell you
 * whether you hold `record.approve`; it cannot tell you that you are the
 * submitter, that somebody else is the assigned reviewer, or that a
 * cascade reaches a type you lack a grant on — and those are the answers
 * the disabled Approve button has to give.
 */
export function getReview(submissionId: string): Promise<SubmissionReview> {
  return client.request<SubmissionReview>(`/api/v1/submissions/${submissionId}/review`);
}

export function approveSubmission(submissionId: string): Promise<Submission> {
  return client.request<Submission>(`/api/v1/submissions/${submissionId}/approve`, {
    method: "POST",
  });
}

export function rejectSubmission(submissionId: string, comment: string): Promise<Submission> {
  return client.request<Submission>(`/api/v1/submissions/${submissionId}/reject`, {
    method: "POST",
    body: { comment },
  });
}

export function requestHandover(
  submissionId: string,
  body: { to_reviewer_id: string; reason: string },
): Promise<Handover> {
  return client.request<Handover>(`/api/v1/submissions/${submissionId}/handover`, {
    method: "POST",
    body,
  });
}

export function listHandovers(
  params: { status?: string | undefined; cursor?: string | undefined } = {},
): Promise<HandoverPage> {
  return client.request<HandoverPage>("/api/v1/handovers", {
    query: { status: params.status, cursor: params.cursor },
  });
}

export function decideHandover(
  handoverId: string,
  approve: boolean,
  comment?: string,
): Promise<Handover> {
  return client.request<Handover>(`/api/v1/handovers/${handoverId}/decide`, {
    method: "POST",
    query: { approve },
    body: { comment: comment ?? null },
  });
}
